<template>
  <div class="member-card-edit">
    <PageTitle
      :title="$t('member.memberCardSetting.edit.title')"
      icon="chevron_left"
      hideBtn
      @iconClick="$router.push({name: 'MemberCardSetting'})"
    />
    <section v-loading="loading" class="side-highlight-container">
      <BaseElForm ref="formRef" :rules="formRules" :model="formData" label-position="top">
        <BaseElFormItem :label="$t('member.memberCardSetting.edit.showLevelName')">
          <BaseElSwitch
            v-model="formData.displayName"
            :inactive-text="$t('member.memberCardSetting.edit.switchOff')"
            :active-text="$t('member.memberCardSetting.edit.switchOn')"
          />
        </BaseElFormItem>
        <BaseElFormItem :label="$t('member.memberCardSetting.edit.showPhone')">
          <BaseElSwitch
            v-model="formData.displayPhone"
            :inactive-text="$t('member.memberCardSetting.edit.switchOff')"
            :active-text="$t('member.memberCardSetting.edit.switchOn')"
          />
        </BaseElFormItem>

        <el-divider />

        <BaseElFormItem :label="$t('member.memberCardSetting.edit.templateName')" prop="name">
          <BaseElInput v-model="formData.name" :disabled="isSystemStyle" />
        </BaseElFormItem>

        <el-divider />

        <FlexLayout align="center" :gap="90" style="margin-bottom: 20px">
          <FlexLayout col>
            <div class="flex items-center" style="gap: 4px">
              <p v-show="!isSystemStyle" class="text-danger">*</p>
              <p class="font-medium text-normal">{{ $t('member.memberCardSetting.edit.cardImage') }}</p>
            </div>
            <p v-show="!isSystemStyle" class="text-sm text-gray-80">{{ $t('member.memberCardSetting.edit.uploadHint') }}</p>
          </FlexLayout>

          <BaseElButton v-if="showControl.btn.aiGenerate" v-featureKey="'admin.imageGenerationTask.create'" type="primary" @click="openAIGenerateModal">
            <FlexLayout align="center" :gap="4">
              {{ $t('member.memberCardSetting.edit.aiGenerateBtn') }}
              <MaterialIcon :size="14">auto_awesome</MaterialIcon>
            </FlexLayout>
          </BaseElButton>
        </FlexLayout>

        <div v-if="loaded" class="flex flex-col gap-[12px]">
          <div
            v-for="(card) in formData.cardSettings"
            v-show="card.level <= 4"
            :key="`${card.style}-${card.level}`"
          >
            <div class="flex items-center gap-[20px]" :name="`${card.style}-${card.level}`">
              <p>{{ $t('member.memberCardSetting.edit.level') }} {{ card.level }}</p>

              <div class="w-[120px] h-[72px] overflow-hidden">
                <template v-if="card.img">
                  <div @click="previewImage(card.img)">
                    <img :id="`img-LV${card.level}`" :src="card.img">
                  </div>
                </template>
                <template v-else>
                  <div class="w-[120px] h-[72px] bg-gray-200 flex items-center justify-center text-gray-500 text-sm">
                    NO IMAGE
                  </div>
                </template>
              </div>

              <div class="flex items-center gap-[12px]">
                <FileInputTextBtn
                  v-if="!isSystemStyle"
                  :key="`${card.style}-${card.level}`"
                  @change="imageChange($event, card.level)"
                >
                  {{ $t('member.memberCardSetting.edit.upload') }}
                </FileInputTextBtn>
                <p v-if="card.img" class="cursor-pointer" @click="onDownload(card)">{{ $t('member.memberCardSetting.edit.download') }}</p>
              </div>
            </div>
          </div>
        </div>
      </BaseElForm>
    </section>

    <WarningDialog
      v-if="modal.warn"
      :title="$t('member.memberCardSetting.edit.warningDialog.title')"
      hideCancel
      :btnString="$t('member.memberCardSetting.edit.warningDialog.confirm')"
      :cancelBtnString="$t('member.memberCardSetting.edit.warningDialog.cancel')"
      @confirm="onConfirmWarning"
      @close="modal.warn = false"
    >
      <template slot="body">
        <div>
          {{ $t('member.memberCardSetting.edit.warningDialog.content') }}
        </div>
      </template>
    </WarningDialog>

    <ImageCropper
      v-if="modal.cropper"
      :image="imgHolder"
      :ratio="[1280,800]"
      @close="onCropperClose"
      @done="getCroppedImage"
    />

    <AIGenerateMemberCardImageModal
      v-if="modal.aiGenerate"
      :aiGenerateConfig="aiGenerateConfig"
      @close="onCloseAiGenModal"
      @generated="onAIGenerated"
    />

    <PageFixedFooter
      :confirmLoading="loading"
      @cancel="$router.push({name: 'MemberCardSetting'})"
      @confirm="onSubmit"
    />
  </div>
</template>

<script>
import { computed, defineComponent, onMounted, reactive, ref, set } from 'vue'
import PageTitle from '@/components/Title/PageTitle.vue'
import PageFixedFooter from '@/components/Footer/PageFixedFooter.vue'
import FileInputTextBtn from '@/components/Button/FileInputTextBtn.vue'
import ImageCropper from '@/components/ImageCropper.vue'
import { get, map } from 'lodash'
import { useRoute, useRouter } from 'vue-router/composables'
import { useBaseForm } from '@/use/useForm'
import { noEmptyRules } from '@/validation'
import { useFetch } from '@/use/fetch'
import { useShop } from '@/use/shop'
import { downloadURI, imgSrc } from '@/utils/helper'
import { GetMemberConfig, UpdateMemberConfig } from '@/api/memberConfig'
import { CreateCustomCardTemplate, FindCustomCardTemplate, UpdateCustomCardTemplate } from '@/api/memberCard'
import WarningDialog from '@/components/Dialog/WarningDialog.vue'
import AIGenerateMemberCardImageModal from './AIGenerateMemberCardImageModal.vue'
import { usePermissions } from '@/use/permissions'
import { api as viewerApi } from 'v-viewer'
import { t } from '@/plugins/i18n/i18n'

export default defineComponent({
  name: 'MemberCardEdit',
  components: { PageTitle, PageFixedFooter, ImageCropper, FileInputTextBtn, WarningDialog, AIGenerateMemberCardImageModal },
  setup () {
    const router = useRouter()
    const route = useRoute()
    const { shopId } = useShop()
    const { simpleFetch } = useFetch()
    const { initFormData, formData, formRef, loading, formSubmit } = useBaseForm()
    const { checkAction } = usePermissions()
    const modal = reactive({
      warn: false,
      cropper: false,
      aiGenerate: false,
    })

    const configData = ref({})
    const selectLevel = ref(null)
    const selectCard = ref(null)
    const imgHolder = ref(null)
    const loaded = ref(false)
    const aiGenerateConfig = ref(null)

    const targetId = computed(() => get(route.params, 'id'))
    const cardStyleName = computed(() => route.params.styleName || 'classic')
    const isSystemStyle = computed(() => {
      if (!targetId.value) return false
      if (targetId.value.includes('@system-')) return true
      return false
    })

    initFormData({
      displayName: true,
      displayPhone: true,
      name: null,
      cardSettings: [],
    })

    // 表單欄位規則
    const formRules = computed(() => {
      if (isSystemStyle.value) return {}
      const rules = {
        name: [noEmptyRules()],
        levelImages: [noEmptyRules()],
      }

      return rules
    })

    const showControl = computed(() => {
      const controls = {
        btn: {
          aiGenerate: checkAction('admin.imageGenerationTask.create'),
        },
      }
      return controls
    })

    const onCropperClose = () => {
      modal.cropper = false
      imgHolder.value = null
    }

    // 需要時再使用：
    // onFormRulesChangeClearValidate(formRef, formRules)

    const getCroppedImage = (e) => {
      const { res, imgDataUrl } = e
      const target = formData.cardSettings[selectLevel.value - 1]
      target.customImageId = res.id
      target.style = 'custom'
      target.img = imgSrc(300, res)
      target.dataUrl = imgDataUrl
      modal.cropper = false
      selectLevel.value = null
    }

    const imageChange = (img, level) => {
      selectLevel.value = level
      imgHolder.value = img
      modal.cropper = true
    }

    const defaultCardImages = () => {
      // 如果是 system style 或 沒有 styleName，預設行為
      // 但新增例外：當沒有 targetId（即建立新範本）時
      // 不要預設上傳本地圖片，讓使用者能直接用 AI 生成或上傳自訂圖片。
      if (!targetId.value) {
        // 建立空的 card 設定，但不指定 img，避免載入預設資源
        // 建立 6 個等級（後端要求），但 UI 只顯示前 4 個
        const images = []
        for (let count = 0; count < 6; count++) {
          const level = count + 1
          images.push({
            style: 'custom',
            level,
            customImageId: null,
            img: '',
          })
        }
        set(formData, 'cardSettings', images)
        return
      }

      if (isSystemStyle.value || !route.params.styleName) {
        const images = []
        // 建立 6 個等級（後端要求），但 UI 只顯示前 4 個
        for (let count = 0; count < 6; count++) {
          const level = count + 1
          let img = require(`@/assets/memberCard/classic/LV${level}.webp`)
          if (cardStyleName.value) img = require(`@/assets/memberCard/${cardStyleName.value}/LV${level}.webp`)
          images.push({
            style: cardStyleName.value,
            level,
            customImageId: null,
            img,
          })
        }

        set(formData, 'cardSettings', images)
      } else {
        formData.cardSettings.forEach(card => {
          if (card.style !== 'custom') card.img = require(`@/assets/memberCard/${card.style}/LV${card.level}.webp`)
          if (card.style === 'custom') card.img = imgSrc(300, card.CustomImage)
        })
      }
    }

    const submitData = computed(() => {
      // 取得等級 4 的設定，用於等級 5 和 6
      const level4Card = formData.cardSettings.find(card => card.level === 4)

      return {
        displayCardLevelName: formData.displayName,
        displayCardPhoneNumber: formData.displayPhone,
        name: formData.name,
        cardSettings: map(formData.cardSettings, (card) => {
          // 等級 5 和 6 使用等級 4 的圖片（後端要求 6 個等級）
          if (card.level > 4 && level4Card) {
            return {
              level: card.level,
              style: level4Card.style,
              customImageId: level4Card.customImageId || undefined,
            }
          }

          // 如果是自訂卡面但沒有 customImageId，跳過（前面的驗證會擋住）
          if (card.style === 'custom' && !card.customImageId) return null

          return {
            level: card.level,
            style: card.style,
            customImageId: card.customImageId || undefined,
          }
        }).filter(Boolean), // 過濾掉 null 值
      }
    })

    // 同步表單資料
    const syncFormData = (data) => {
      formData.name = data.name
      formData.cardSettings = data.cardSettings
      aiGenerateConfig.value = get(data, 'cardSettings[0].CustomImage.ImageGenerationTask.input.memberCardSetting', {})
    }

    // 送出表單
    const onSubmit = async () => {
      const handler = async (data, { loading }) => {
        // 驗證：自訂卡面的所有等級都必須上傳圖片
        if (!isSystemStyle.value) {
          const missingImages = formData.cardSettings
            .filter(card => card.level <= 4 && card.style === 'custom' && !card.customImageId)

          if (missingImages.length > 0) {
            const levels = missingImages.map(card => card.level).join('、')
            window.$message.error(t('member.memberCardSetting.edit.uploadImageError', { levels }))
            loading.value = false
            return
          }
        }

        let apiMethod
        if (!targetId.value) apiMethod = CreateCustomCardTemplate
        if (targetId.value) apiMethod = UpdateCustomCardTemplate

        try {
          await simpleFetch(UpdateMemberConfig, {
            shopId: shopId.value,
            displayCardLevelName: get(data, 'displayCardLevelName'),
            displayCardPhoneNumber: get(data, 'displayCardPhoneNumber'),
          })
          if (!isSystemStyle.value) {
            await simpleFetch(apiMethod, {
              shopId: shopId.value,
              id: targetId.value,
              name: get(data, 'name'),
              cardSettings: get(data, 'cardSettings'),
            })
          }

          // await Promise.all([
          //   simpleFetch(UpdateMemberConfig, {
          //     shopId: shopId.value,
          //     displayCardLevelName: get(data, 'displayCardLevelName'),
          //     displayCardPhoneNumber: get(data, 'displayCardPhoneNumber'),
          //   }),
          //   simpleFetch(apiMethod, {
          //     shopId: shopId.value,
          //     id: targetId.value,
          //     name: get(data, 'name'),
          //     cardSettings: get(data, 'cardSettings'),
          //   }),
          // ])

          loading.value = false
          router.push({ name: 'MemberCardSetting' })
        } catch (error) {
          console.log(error)
        }
      }
      await formSubmit(handler, submitData.value, { loading })
    }

    const onDownload = (card) => {
      if (card.style !== 'custom') {
        selectCard.value = card
        modal.warn = true
        return
      }
      let newPage = false
      if (card.style === 'custom') newPage = true
      downloadURI(card.img, `LV${card.level}`, newPage)
    }

    const onConfirmWarning = () => {
      const card = selectCard.value
      downloadURI(card.img, `LV${card.level}`, true)
      selectCard.value = null
      modal.warn = false
    }

    const openAIGenerateModal = () => {
      modal.aiGenerate = true
    }

    const onCloseAiGenModal = (config) => {
      // 保存 modal 傳回的配置
      if (config) {
        aiGenerateConfig.value = config
      }
      modal.aiGenerate = false
    }

    const onAIGenerated = async (generateData) => {
      const images = map(generateData.data, (item) => {
        return {
          filename: get(item, 'Image.filename'),
          id: get(item, 'imageId'),
        }
      })

      // 替換現有各等級圖示
      images.forEach((img, index) => {
        const level = index + 1
        const target = formData.cardSettings[level - 1]
        if (target) {
          target.customImageId = img.id
          target.style = 'custom'
          target.img = imgSrc(600, img)
        }
      })
    }

    const previewImage = (img) => {
      viewerApi({
        images: [img],
        options: {
          toolbar: false,
          navbar: false,
          title: false,
        },
      })
    }

    onMounted(async () => {
      await simpleFetch(GetMemberConfig, { shopId: shopId.value }, (res) => {
        configData.value = res
        formData.displayName = get(res, 'displayCardLevelName')
        formData.displayPhone = get(res, 'displayCardPhoneNumber')
      })
      if (!isSystemStyle.value && route.params.styleName) {
        await simpleFetch(FindCustomCardTemplate, { shopId: shopId.value, id: targetId.value },
          (res) => {
            syncFormData(res)
          },
        )
      } else if (isSystemStyle.value) {
        formData.name = cardStyleName.value
      }

      defaultCardImages()
      loaded.value = true
    })

    return {
      loading,
      formRef,
      formData,
      formRules,
      onSubmit,
      modal,
      imageChange,
      isSystemStyle,
      imgHolder,
      getCroppedImage,
      imgSrc,
      onDownload,
      submitData,
      selectLevel,
      loaded,
      showControl,
      onConfirmWarning,
      onCropperClose,
      openAIGenerateModal,
      onCloseAiGenModal,
      onAIGenerated,
      previewImage,
      aiGenerateConfig,
    }
  },
})
</script>

<style lang="postcss" scoped>

</style>
